/**
 * TestimonialForm — formulario público para que un visitante envíe su
 * testimonio. Consume `useSubmitTestimonial` y `validatePublicTestimonial`
 * (ambos ya definidos en `./api.ts`) para el flujo:
 *   1. usuario escribe → validación local en `onBlur` + al submit.
 *   2. INSERT en `testimonials` con `is_published=false` forzado por RLS.
 *   3. Al éxito, el form se limpia y se muestra un mensaje estético
 *      confirmando que Cecilia lo revisará antes de publicarlo.
 *
 * A11y:
 *  - Cada campo usa `<FormField>` del DS (label + helper + error).
 *  - El rating es un radiogroup accesible con teclado (`role="radiogroup"` +
 *    `role="radio"` + `aria-checked`; el `<label>` cubre el input tapado).
 *  - El mensaje de éxito lleva `role="status" aria-live="polite"` para que
 *    los SR lo anuncien sin robar foco.
 *
 * Reglas del DS respetadas: cero hex literals, tokens `bg-cream-*`,
 * `text-primary-*`, `text-gold-*`, tipografía `font-display` para heading,
 * bordes `border-gold-500/*`, sombra `shadow-elev1`, esquinas
 * `rounded-lg`. Sin dependencias nuevas.
 */
import { useState, type ChangeEvent, type FormEvent } from 'react';
import { Sparkles, Star } from 'lucide-react';

import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { FormField } from '../../components/ui/FormField';
import { Input } from '../../components/ui/Input';
import { Textarea } from '../../components/ui/Textarea';
import { useToast } from '../../components/ui/ToastProvider';
import { cn } from '../../lib/cn';

import {
  useSubmitTestimonial,
  validatePublicTestimonial,
  type PublicTestimonialInput,
  type TestimonialValidation,
} from './api';

const BODY_MAX = 2000;
const NAME_MAX = 120;

type FieldErrors = TestimonialValidation['errors'];

interface DraftState {
  author_name: string;
  body: string;
  rating: number | null;
}

const EMPTY_DRAFT: DraftState = {
  author_name: '',
  body: '',
  rating: null,
};

// ---------------------------------------------------------------------------
// Rating input (5 estrellas, accesible)
// ---------------------------------------------------------------------------

interface RatingInputProps {
  value: number | null;
  onChange: (next: number | null) => void;
  disabled?: boolean;
}

/**
 * Radiogroup de 5 estrellas. `Star` de lucide se pinta con `fill-gold-500`
 * cuando la estrella actual es ≤ al valor seleccionado; opacidad ligera al
 * hacer hover. Permite deseleccionar clickeando la estrella activa (opcional
 * — el rating es opcional para el schema).
 */
function RatingInput({ value, onChange, disabled }: RatingInputProps) {
  const stars = [1, 2, 3, 4, 5] as const;
  return (
    <div role="radiogroup" aria-label="Calificación" className="inline-flex items-center gap-1">
      {stars.map((n) => {
        const selected = value !== null && n <= value;
        return (
          <button
            key={n}
            type="button"
            role="radio"
            aria-checked={value === n}
            aria-label={`${n} estrella${n > 1 ? 's' : ''}`}
            disabled={disabled}
            onClick={() => onChange(value === n ? null : n)}
            className={cn(
              'inline-flex h-8 w-8 items-center justify-center rounded-pill',
              'transition-colors duration-2 ease-out',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-500/60',
              'disabled:cursor-not-allowed disabled:opacity-60',
            )}
          >
            <Star
              className={cn(
                'h-5 w-5 transition-colors duration-2 ease-out',
                selected
                  ? 'fill-gold-500 text-gold-500'
                  : 'fill-none text-primary-700/40 hover:text-gold-500/70',
              )}
              strokeWidth={1.75}
            />
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Formulario principal
// ---------------------------------------------------------------------------

export interface TestimonialFormProps {
  /** Clase adicional para el `<Card>` exterior. */
  className?: string;
  /**
   * Cuando el formulario vive dentro de un `<Modal>`, el consumidor pasa
   * un `onClose` para que el estado de éxito ofrezca "Cerrar" en lugar de
   * "Enviar otro testimonio". Uso standalone (embebido en la página) puede
   * omitirlo y mantener el reset local.
   */
  onClose?: () => void;
  /**
   * Cuando el form vive dentro de un `<Modal>` que ya renderiza su propia
   * cabecera (título/descripción), pasa `chromeless` para omitir el
   * `<Card>` externo y el `<Card.Header>` — así no se duplican títulos.
   */
  chromeless?: boolean;
}

export function TestimonialForm({ className, onClose, chromeless = false }: TestimonialFormProps) {
  const [draft, setDraft] = useState<DraftState>(EMPTY_DRAFT);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [submitted, setSubmitted] = useState(false);

  const submit = useSubmitTestimonial();
  const { toast } = useToast();

  const patchDraft = (patch: Partial<DraftState>) => setDraft((prev) => ({ ...prev, ...patch }));

  const handleAuthorChange = (e: ChangeEvent<HTMLInputElement>) => {
    patchDraft({ author_name: e.target.value });
    if (errors.author_name) setErrors((p) => ({ ...p, author_name: undefined }));
  };

  const handleBodyChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    patchDraft({ body: e.target.value });
    if (errors.body) setErrors((p) => ({ ...p, body: undefined }));
  };

  const handleRatingChange = (next: number | null) => {
    patchDraft({ rating: next });
    if (errors.rating) setErrors((p) => ({ ...p, rating: undefined }));
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const input: PublicTestimonialInput = {
      author_name: draft.author_name,
      body: draft.body,
      rating: draft.rating,
    };
    const v = validatePublicTestimonial(input);
    if (!v.ok) {
      setErrors(v.errors);
      return;
    }
    submit.mutate(input, {
      onSuccess: () => {
        setDraft(EMPTY_DRAFT);
        setErrors({});
        setSubmitted(true);
      },
      onError: (err) => {
        toast({
          variant: 'warning',
          title: 'No pudimos enviar tu testimonio',
          description: err.message,
        });
      },
    });
  };

  const resetForNew = () => {
    setSubmitted(false);
    setDraft(EMPTY_DRAFT);
    setErrors({});
  };

  // Contenido interno del form (o del estado de éxito). Se extrae para
  // renderizarlo con o sin envoltorio de Card según `chromeless`.
  const inner = submitted ? (
    <div
      role="status"
      aria-live="polite"
      className="flex flex-col items-center gap-4 rounded-lg border border-gold-500/40 bg-cream-100 px-6 py-8 text-center"
    >
      <span
        aria-hidden="true"
        className="inline-flex h-14 w-14 items-center justify-center rounded-pill border border-gold-500/50 bg-cream-50 text-gold-500"
      >
        <Sparkles className="h-6 w-6" strokeWidth={1.5} />
      </span>
      <h4 className="font-display text-h4 text-primary-900">¡Gracias por compartir tu luz!</h4>
      <p className="max-w-md text-body text-primary-900/80">
        Tu testimonio ha sido enviado y está pendiente de revisión por Cecilia.
      </p>
      {onClose ? (
        <Button variant="outline" size="md" onClick={onClose}>
          Cerrar
        </Button>
      ) : (
        <Button variant="outline" size="md" onClick={resetForNew}>
          Enviar otro testimonio
        </Button>
      )}
    </div>
  ) : (
    <form className="flex flex-col gap-5" onSubmit={handleSubmit} noValidate>
      <FormField
        label="Tu nombre"
        required
        error={errors.author_name ?? undefined}
        helper={`${draft.author_name.length}/${NAME_MAX} caracteres.`}
      >
        <Input
          type="text"
          autoComplete="name"
          value={draft.author_name}
          onChange={handleAuthorChange}
          placeholder="Como te gustaría aparecer"
          maxLength={NAME_MAX}
        />
      </FormField>

      <FormField
        label="Tu mensaje"
        required
        error={errors.body ?? undefined}
        helper={`${draft.body.length}/${BODY_MAX} caracteres.`}
      >
        <Textarea
          rows={5}
          maxLength={BODY_MAX}
          value={draft.body}
          onChange={handleBodyChange}
          placeholder="Cuéntanos qué te movió en tu proceso con Cecilia."
          resize="vertical"
        />
      </FormField>

      <FormField
        label="Calificación"
        optional
        error={errors.rating ?? undefined}
        helper="Elige entre 1 y 5 estrellas si quieres calificar la experiencia."
      >
        <RatingInput
          value={draft.rating}
          onChange={handleRatingChange}
          disabled={submit.isPending}
        />
      </FormField>

      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        {onClose && (
          <Button
            type="button"
            variant="outline"
            size="md"
            onClick={onClose}
            disabled={submit.isPending}
          >
            Cancelar
          </Button>
        )}
        <Button
          type="submit"
          variant="primary"
          size="md"
          loading={submit.isPending}
          leftIcon={<Sparkles aria-hidden="true" strokeWidth={1.5} />}
        >
          Compartir mi luz
        </Button>
      </div>
    </form>
  );

  // Cuando el consumidor pide `chromeless` (ej. dentro de un `<Modal>`
  // que ya provee título/descripción y su propio contorno), devolvemos
  // sólo el bloque interno para no duplicar el chrome.
  if (chromeless) return inner;

  return (
    <Card as="div" className={cn('border border-gold-500/25 bg-cream-50 shadow-elev1', className)}>
      <Card.Header>
        <div className="flex items-start gap-3">
          <span
            aria-hidden="true"
            className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-pill border border-gold-500/40 bg-cream-100 text-gold-500"
          >
            <Sparkles className="h-4 w-4" strokeWidth={1.5} />
          </span>
          <div className="flex flex-col gap-1">
            <h3 className="font-display text-h3 text-primary-900">Comparte tu experiencia</h3>
            <p className="text-body text-primary-900/70">
              Tu testimonio inspira a otras almas que buscan sanación. Cecilia lo leerá con cariño
              antes de publicarlo.
            </p>
          </div>
        </div>
      </Card.Header>

      <Card.Body>{inner}</Card.Body>
    </Card>
  );
}
